import { useState } from "react";
import { toast } from "sonner";
import { Ban, X, Check } from "lucide-react";

/**
 * Blocco 4 · F · Modal "Annulla meta/azione".
 *
 * Props:
 *  - entity: { title }
 *  - onClose(): void
 *  - onConfirm({ reason }): Promise<void>
 */
export default function MetaCancelModal({ entity, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form"); // 'form' | 'confirm'

  const goToConfirm = () => setStep("confirm");

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm({ reason: reason.trim() || null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="meta-cancel-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-semibold inline-flex items-center gap-2">
            <Ban size={20} className="opacity-60"/> Annulla
          </h3>
          <button onClick={onClose} aria-label="Chiudi"><X size={20}/></button>
        </div>
        <div className="text-sm opacity-80">"{entity.title}"</div>

        {step === "form" && (
          <>
            <label className="block text-sm">
              <div className="opacity-70 mb-1">Motivo <span className="opacity-60">(opzionale)</span></div>
              <textarea className="pxos-input" rows={4} value={reason} maxLength={2000}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Perché stai lasciando andare questa meta? (facoltativo, ma utile a te futura)"
                        data-testid="meta-cancel-reason"/>
            </label>
            <div className="text-[11px] opacity-70 rounded p-2" style={{ background: "#F3F4F6" }}>
              L'annullamento la esclude dai calcoli di progresso ma resta consultabile nel tab "Annullate".
              Puoi sempre ripristinarla.
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={onClose} className="text-sm underline opacity-70">Annulla azione</button>
              <button onClick={goToConfirm} className="pxos-btn-primary text-sm" data-testid="meta-cancel-next">
                Continua
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="text-sm rounded-md p-3" style={{ background: "#FEF3C7", color: "#78350F" }}>
              <b>Confermi annullamento?</b><br/>
              La meta verrà spostata nel tab "Annullate" ed esclusa dai calcoli di progresso.
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setStep("form")} className="text-sm underline opacity-70">Torna indietro</button>
              <button disabled={busy} onClick={submit}
                      className="pxos-btn-primary text-sm"
                      style={{ background: "#6B7280" }}
                      data-testid="meta-cancel-confirm">
                <Check size={14}/> Sì, annulla
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
